// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/earlyCheckin.ts
// 🔧 PURPOSE: IL CHECK-IN ANTICIPATO NELL'APP: quali fasce si possono
//             ancora comprare e quando proporlo in Home.
//
//             Decisioni del proprietario (set 2026), le stesse del channel
//             manager (lib/early-checkin-rules.ts, che il pagamento
//             ricontrolla sempre):
//             - si compra anche IL GIORNO DELL'ARRIVO, fascia per fascia,
//               fino a 30 minuti prima dell'orario (ora di ROMA, non quella
//               del telefono); prima del giorno dell'arrivo come sempre;
//             - le fasce passate o spente dal gestore non si comprano mai;
//             - se chi parte dallo stesso appartamento ha il late check-out,
//               si entra al più un'ora prima del check-in: il catalogo manda
//               quelle fasce spente con `unavailableReason: 'apartment_busy'`
//               (e l'ora in `earlyCheckinMinTime`).
//             Il catalogo arriva già calcolato dal server, ma resta aperto
//             per minuti: qui si ricalcola sull'orologio della Home, così
//             una fascia che scade mentre l'ospite guarda si spegne da sola.
//
//             Niente notifiche la mattina: la proposta sta nella Home
//             ("Adesso per te"), il giorno prima dell'arrivo e quello
//             dell'arrivo (earlyCheckinOffer).
// ============================================

import type { AppExtra, ExtrasCatalog, GuestBooking, PurchasedExtra } from './guestApi';
import { validTime } from './houseTimes';
import { dayKeyDiff, romeDayKey } from './romeDay';

export const EARLY_CHECKIN_ID = 'early_checkin';

/** Dall'app si compra fino a 30 minuti prima della fascia (lo stesso numero del server). */
export const EARLY_CHECKIN_LEAD_MINUTES = 30;

/** Perché una fascia oraria non si può scegliere adesso. */
export type SlotBlock = 'unavailable' | 'past' | 'too_close' | 'apartment_busy';

type Slot = { time: string; price: number; isAvailable: boolean; unavailableReason?: string };

const SERVER_BLOCKS: SlotBlock[] = ['unavailable', 'past', 'too_close', 'apartment_busy'];

const PARTS_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Rome',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** Di quanti minuti Roma è avanti su UTC in quell'istante (60 o 120). */
function romeOffsetMinutes(instant: number): number {
  const parts = PARTS_FMT.formatToParts(new Date(instant));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return Math.round((wall - Math.floor(instant / 1000) * 1000) / 60000);
}

/**
 * L'istante vero di "quel giorno di Roma, a quell'ora di Roma" (col cambio
 * dell'ora compreso: due giri, il secondo con lo scarto giusto). Null se
 * giorno od orario non si leggono.
 */
export function romeInstant(dayKey: string | null | undefined, hhmm: string | null | undefined): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ''));
  const time = validTime(hhmm);
  if (!d || !time) return null;
  const [h, m] = time.split(':').map(Number);
  const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), h, m);
  let guess = wall - romeOffsetMinutes(wall) * 60000;
  guess = wall - romeOffsetMinutes(guess) * 60000;
  return new Date(guess);
}

/** Il giorno d'arrivo di Roma: "AAAA-MM-GG" resta com'è, un ISO si porta a Roma. */
export function arrivalDayOf(checkIn: string | null | undefined): string | null {
  if (!checkIn) return null;
  const s = String(checkIn).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return romeDayKey(s) || null;
}

export interface SlotStateInput {
  serviceId: string;
  /** Giorno dell'arrivo ("AAAA-MM-GG" o ISO). */
  checkIn?: string | null;
  now: Date;
}

/**
 * Perché QUESTA fascia non si può scegliere adesso (null = si può).
 *
 * - `isAvailable: false`: il motivo è quello del server (spenta dal gestore,
 *   passata, troppo vicina, appartamento occupato); senza motivo è "non
 *   disponibile" come sempre.
 * - Check-in anticipato: una fascia già passata (anche nei giorni dopo
 *   l'arrivo) o a meno di 30 minuti, sull'ora di Roma.
 * - Gli altri servizi a fasce: come prima, solo il giorno dell'arrivo una
 *   fascia già passata (ma sull'ora di Roma, non su quella del telefono).
 */
export function slotBlock(slot: Slot, input: SlotStateInput): SlotBlock | null {
  if (!slot.isAvailable) {
    const reason = slot.unavailableReason as SlotBlock | undefined;
    return reason && SERVER_BLOCKS.indexOf(reason) >= 0 ? reason : 'unavailable';
  }
  const arrivalDay = arrivalDayOf(input.checkIn);
  if (!arrivalDay) return null;
  if (input.serviceId !== EARLY_CHECKIN_ID && romeDayKey(input.now) !== arrivalDay) return null;
  const at = romeInstant(arrivalDay, slot.time);
  if (!at) return null;
  const remaining = at.getTime() - input.now.getTime();
  if (remaining <= 0) return 'past';
  if (input.serviceId === EARLY_CHECKIN_ID && remaining < EARLY_CHECKIN_LEAD_MINUTES * 60000) return 'too_close';
  return null;
}

/** Le fasce del servizio che si possono scegliere adesso. */
export function usableSlots(item: AppExtra, checkIn: string | null | undefined, now: Date): Slot[] {
  return (item.options.timeSlotOptions || []).filter((s) => slotBlock(s, { serviceId: item.serviceId, checkIn, now }) === null);
}

/** La proposta della Home: da che ora si entra e il prezzo più basso. */
export interface EarlyCheckinOffer {
  /** La prima fascia ancora acquistabile ("12:00"). */
  fromTime: string;
  /** Il prezzo più basso fra le fasce acquistabili (sempre > 0). */
  price: number;
}

export interface EarlyCheckinOfferInput {
  catalog: ExtrasCatalog | null | undefined;
  booking: Pick<GuestBooking, 'checkIn' | 'declaredArrivalTime' | 'purchasedExtras'> | null | undefined;
  now: Date;
  /** L'ora di check-in della struttura (senza l'anticipo): state.houseCheckInTime. */
  houseCheckInTime: string;
}

const bought = (list: PurchasedExtra[] | null | undefined): boolean =>
  (list || []).some((e) => e.serviceId === EARLY_CHECKIN_ID);

/**
 * "Arrivi prima? Entra già dalle 12:00 · da 20 €": la card della Home.
 *
 * C'è solo quando TUTTO questo è vero:
 * - oggi (Roma) è il giorno prima dell'arrivo o il giorno dell'arrivo;
 * - il servizio c'è ed è in vendita adesso (catalogo), non è già comprato;
 * - resta almeno una fascia acquistabile adesso, con un prezzo (una fascia
 *   a 0 € dall'app non si paga: "chiedicelo in chat");
 * - l'ospite non ha dichiarato di arrivare DOPO l'orario di check-in (chi ha
 *   scritto "arrivo alle 18" non ha bisogno di entrare alle 12). Senza
 *   orario dichiarato la card c'è.
 */
export function earlyCheckinOffer(input: EarlyCheckinOfferInput): EarlyCheckinOffer | null {
  const { catalog, booking, now } = input;
  if (!catalog || !catalog.success || !booking) return null;
  const arrivalDay = arrivalDayOf(booking.checkIn);
  const today = romeDayKey(now);
  if (!arrivalDay || !today) return null;
  const days = dayKeyDiff(arrivalDay, today);
  if (days !== 0 && days !== 1) return null;

  const item = (catalog.items || []).filter((i) => i.serviceId === EARLY_CHECKIN_ID)[0];
  if (!item || !item.available || item.alreadyPurchased) return null;
  if (bought(catalog.purchasedExtras) || bought(booking.purchasedExtras)) return null;

  const declared = validTime(booking.declaredArrivalTime);
  const house = validTime(input.houseCheckInTime);
  if (declared && house && declared > house) return null;

  const slots = usableSlots(item, booking.checkIn, now).filter((s) => s.price > 0);
  if (slots.length === 0) return null;
  let fromTime = validTime(slots[0].time) || slots[0].time;
  let price = slots[0].price;
  for (let i = 1; i < slots.length; i++) {
    const time = validTime(slots[i].time) || slots[i].time;
    if (time < fromTime) fromTime = time;
    if (slots[i].price < price) price = slots[i].price;
  }
  return { fromTime, price };
}
